// askSmart-ui/src/pages/TOSP/components/NewTaxBudgetModal.tsx
import React, { useEffect, useState } from "react";
import { parseNumeric, validateNewYear } from "../validation";

interface NewTaxBudgetModalProps {
  isOpen: boolean;
  /** Years already open across the portfolio, for the duplicate / ordering checks. */
  existingYears: number[];
  /**
   * Years that are open but carry no Budget PA on any unit.
   *
   * Allowed through the duplicate check: a year opened before its baseline
   * existed is a job half done, and re-running fills it in rather than
   * overwriting anything.
   */
  unbudgetedYears?: number[];
  onClose: () => void;
  /** Awaited, so the button can hold until the writes are done. */
  onConfirm: (year: number, percentageIncrease: number) => void | Promise<void>;
}

// Opens a new tax year across every unit at once, by rolling the latest one
// forward at a set increase.
export default function NewTaxBudgetModal({
  isOpen,
  existingYears,
  unbudgetedYears = [],
  onClose,
  onConfirm,
}: NewTaxBudgetModalProps) {
  const [year, setYear] = useState("");
  const [increase, setIncrease] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Opening a year writes a budget to every roll share and a record to every
  // unit — long enough that a still button reads as a dead one.
  const [busy, setBusy] = useState(false);

  const latestYear = existingYears.length ? Math.max(...existingYears) : null;
  /** An open-but-empty year is the job in front of you, so it's what's offered.
   *  The caller narrows this to the newest year; earliest-first would offer a
   *  long-closed year that will never carry a budget. */
  const pending = unbudgetedYears.length ? Math.max(...unbudgetedYears) : null;

  // Opening is the only thing that resets this form.
  //
  // NOT on latestYear/pending: both change the moment the run this modal
  // started writes its first budget, and re-running the reset mid-submit
  // cleared `busy` and blanked the percentage — putting a live Create button
  // back under the cursor of someone already waiting. Every extra click then
  // launched another full pass, which is what looked like a loop.
  useEffect(() => {
    if (!isOpen) return;
    // Default to the year waiting to be budgeted, else the year after the
    // latest — the only other one that's valid.
    setYear(
      pending !== null ? String(pending) : latestYear === null ? "" : String(latestYear + 1),
    );
    setIncrease("");
    setErrors({});
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next: Record<string, string> = {};

    const { year: parsedYear, error: yearError } = validateNewYear(
      year,
      // An unbudgeted year isn't a duplicate — there's nothing there to clash
      // with, and re-running is how it gets filled in.
      existingYears.filter((y) => !unbudgetedYears.includes(y)),
    );
    if (yearError) next.year = yearError;

    const parsedIncrease = parseNumeric(increase);
    if (!increase.trim()) {
      next.increase = "Percentage Increase is required.";
    } else if (parsedIncrease === null) {
      next.increase = "Percentage Increase must be a number.";
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setBusy(true);
    try {
      await onConfirm(parsedYear as number, parsedIncrease as number);
    } finally {
      // On success the parent closes this and the reset above clears it; on
      // failure the button has to come back so the run can be retried.
      setBusy(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay tosp-modal-nested">
      <div className="smrtdocs-modal" style={{ width: "440px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>New Tax Budget</h2>
        </div>
        <form className="smrtdocs-modal-body" onSubmit={handleSubmit} noValidate autoComplete="off">
          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-budget-year">
                Budget Year <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-budget-year"
                className="manage-input"
                autoComplete="off"
                inputMode="numeric"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                autoFocus
              />
              {errors.year && <p className="tosp-field-error">{errors.year}</p>}
            </div>

            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-budget-increase">
                Percentage Increase <span className="tosp-required">*</span>
              </label>
              <div className="tosp-affix-wrap">
                <input
                  id="tosp-budget-increase"
                  className="manage-input tosp-has-suffix"
                  autoComplete="off"
                  inputMode="decimal"
                  placeholder="e.g. 2.0"
                  value={increase}
                  onChange={(e) => setIncrease(e.target.value)}
                />
                <span className="tosp-affix tosp-affix-suffix">%</span>
              </div>
              {errors.increase && <p className="tosp-field-error">{errors.increase}</p>}
            </div>
          </div>

          <p className="tosp-field-hint" style={{ marginBottom: 16 }}>
            {pending !== null
              ? "Each unit's is set from its last billed year's Total PA plus the increase."
              : latestYear === null
                ? "The first tax year opens every unit's roll with blank amounts, ready for Budget PA."
                : `Every unit's Budget PA is set to its last billed year's Total PA plus the increase. Interim and Final start blank.`}
          </p>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {/* Cancel is held too: the writes are already in flight, and
                closing mid-run would hide a half-opened year. */}
            <button type="button" className="btn-cancel" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? (
                <>
                  <span className="tosp-spinner" aria-hidden="true" />
                  Creating…
                </>
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
